import type { Guide } from "./guide";

/** Guide 10: the checklist from test mode to your users. Caps, webhooks and users are kept per mode, so each is set again. */
export const GOING_LIVE: Guide = {
  slug: "going-live",
  samples: "live",
  title: "Going live",
  nav: "Going live",
  summary:
    "The checklist from test mode to your users: a live key, CI's own key, caps, webhooks, a deploy that waits for its tests, and the way back.",
  minutes: 10,
  lede: "Everything so far ran in test mode. Live mode starts empty, because keys, caps, webhooks and users are kept per mode. Each step is one item of the checklist.",
  lines: ["keys", "deploy", "usage", "events"],
  steps: [
    {
      id: "key",
      title: "Get a live key",
      body: [
        "In the [console](/developers/console), switch to live mode and create a key. A key takes its mode from the key that made it ([`keys`](/developers/keys)).",
      ],
      then: "Keep it on your server as `VESTRA_LIVE_KEY`: it reaches your users and real money.",
    },
    {
      id: "ci",
      title: "Give CI its own key",
      body: ["A `setup` key deploys and tests, and can never act for a user."],
      doors: {
        ts: { sample: "guide-live-ci" },
        py: { sample: "guide-live-ci" },
        cli: { sample: "guide-live-ci" },
        http: { sample: "guide-live-ci" },
        mcp: { note: "Keys stay with people: no MCP tool makes one." },
      },
      then: "Its secret is shown this once: keep it in your CI's secrets.",
      prints: [
        "Made key_3vd (ci), live mode.",
        "Its secret, shown this once: vk_live_…",
      ].join("\n"),
    },
    {
      id: "caps",
      title: "Cap the spend",
      body: [
        "Caps are kept per mode, so set them again for live ([`usage`](/developers/usage)).",
      ],
      doors: {
        ts: { sample: "guide-live-caps" },
        py: { sample: "guide-live-caps" },
        cli: { sample: "guide-live-caps" },
        http: { sample: "guide-live-caps" },
        mcp: {
          note: "Caps stay with people: set them from an SDK, the CLI or HTTP.",
        },
      },
      then: "Work past a cap is refused with `402`, and `usage.cap_reached` reaches your webhooks.",
    },
    {
      id: "webhooks",
      title: "Point your webhooks at production",
      body: [
        "Webhooks are kept per mode too: your test endpoint never hears a live user.",
      ],
      doors: {
        ts: { sample: "guide-live-webhooks" },
        py: { sample: "guide-live-webhooks" },
        cli: { sample: "guide-live-webhooks" },
        http: { sample: "guide-live-webhooks" },
        mcp: { note: "Webhooks are set up from an SDK, the CLI or HTTP." },
      },
      then: "Live events reach your server, signed with this endpoint's own secret.",
      prints: [
        "Sending task.completed, usage.cap_reached to https://api.tripnest.example/vestra as whk_8nd.",
        "Its signing secret, shown this once: whsec_…",
      ].join("\n"),
    },
    {
      id: "ship",
      title: "Ship it live",
      body: [
        "A live deploy plays its tests first ([`deploy`](/developers/deploy)).",
      ],
      doors: {
        ts: { sample: "guide-live-ship" },
        py: { sample: "guide-live-ship" },
        cli: { sample: "guide-live-ship" },
        http: { sample: "guide-live-ship" },
        mcp: {
          note: "Going live is a person's to type: `/v1/mcp` and `vestra mcp` deploy to test mode only.",
        },
      },
      then: "Only a pass puts the new version in front of your users. A failure leaves the version before running.",
      prints: [
        "travel-help version 5: changed instructions.",
        "  promotes in live mode once its tests pass: vestra events trun_6tw.",
      ].join("\n"),
    },
    {
      id: "back",
      title: "Know the way back",
      body: ["A rollback puts the version before back at once."],
      doors: {
        ts: { sample: "guide-testing-rollback" },
        py: { sample: "guide-testing-rollback" },
        cli: { sample: "guide-testing-rollback" },
        http: { sample: "guide-testing-rollback" },
        mcp: {
          note: "`/v1/mcp` never deploys or rolls back: that stays with you, with a live key.",
        },
      },
      then: "The version before is live again, with no tests to wait for.",
      prints: "travel-help: version 4 is live again.",
    },
    {
      id: "whole",
      title: "The whole thing",
      body: [
        "The checklist as one program, run once before your first live user.",
      ],
      doors: {
        ts: { sample: "guide-live-whole" },
        py: { sample: "guide-live-whole" },
        cli: { sample: "guide-live-whole" },
        http: { sample: "guide-live-whole" },
        mcp: {
          note: "Going live stays with people: keys, caps and live deploys are never an agent's over MCP.",
        },
      },
      then: "Your users meet a tested version, inside your caps, with your server hearing every step.",
    },
  ],
};
